'use client';

import { ChangeEvent, useState, useEffect } from 'react';

import { useI18n } from '@northladder/i18n';
import {
  cleanupDoubleSpace,
  validateAndCleanInputText,
  useDebouncedCallback,
  validate,
} from '@northladder/utilities';

import { IInputElement } from '../../types';

import { TextInput } from './TextInput';

interface IInputTextItemProps extends IInputElement {
  // Add more `InputTextElement` props here
}

/**
 * -----------------------------------------------------------------------------
 * This is used to create the underlying debounced `TextInput` that...
 * - Captures user input, validates it based on provided constraints config.
 * - Updates the parent form the debounce delay (default 1s) with errors data
 *   and updated input.
 * @deprecated- To be deleted later
 */
export function InputTextElement({
  autoFocus,
  className,
  classNameInput,
  classNameLabel,
  constraints,
  editable,
  error,
  hasClearButton,
  helpText,
  iconEnd,
  iconStart,
  isLabelEnabled,
  label,
  labelPostFix,
  name,
  onChangeCallback,
  onError,
  onBlurCallback,
  onFocus,
  placeholder,
  showIsRequired,
  subLabel,
  type,
  value = '',
  valuePrefix,
}: IInputTextItemProps) {
  const { locale } = useI18n();

  const {
    minLength,
    maxLength = 40,
    regExp,
    required,
    format,
  } = constraints || {};

  const [inputValue, setInputValue] = useState(value);

  /**
   * Skip debounce for email and password fields to ensure immediate updates.
   * Use constraints?.type === 'password' so that when the password visibility
   * toggle switches the input type to 'text', we still validate on change.
   */
  const shouldSkipDebounce =
    type === 'email' ||
    type === 'password' ||
    constraints?.type === 'password';

  const onInputChangeDebounced = useDebouncedCallback(onChangeCallback, {
    wait: 1000,
  });

  // Sync with external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleValidateOnChange = (newValue: string) => {
    const updatedText = validateAndCleanInputText({
      text: newValue,
      format,
      regExp,
    });

    setInputValue(updatedText);

    // For email/password, sync form state immediately (error is set from validate in handleInputChanged)
    if (shouldSkipDebounce) {
      // For email/password, call immediately but deferred to avoid render issues
      queueMicrotask(() => {
        onChangeCallback({ name, value: updatedText.trim() });
      });
    } else {
      onInputChangeDebounced({ name, value: updatedText.trim() });
    }

    return updatedText;
  };

  const handleInputChanged = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const text = event.target.value;
    const updatedText = handleValidateOnChange(text);

    // For email/password, re-validate on change so setCustomValidity is updated.
    // Otherwise, if user blurred empty then typed a valid value and submits with Enter,
    // the input never blurs again and the native validation message persists.
    if (shouldSkipDebounce && constraints) {
      event.target.value = updatedText.trim();
      const inputError = validate({
        target: event.target,
        constraints,
        locale,
      });
      onError({ name, error: inputError });
    }
  };

  const handleInputFocus = () => {
    onError({ name, error: undefined });
    onFocus?.();
  };

  const handleInputBlur = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let updatedText = event.target.value;

    // Validate and clean the input text
    updatedText = validateAndCleanInputText({
      text: updatedText,
      format,
      regExp,
    });

    if (updatedText) {
      updatedText = cleanupDoubleSpace(updatedText);
    }

    // Update internal state immediately
    setInputValue(updatedText);

    // For email/password fields, immediately sync form state before validation
    // This ensures validation sees the current value, not stale state
    if (shouldSkipDebounce) {
      onChangeCallback({ name, value: updatedText.trim() });
    }

    // Update event target value to cleaned text before validation
    // This ensures validate() checks the cleaned value, not the original input
    event.target.value = updatedText.trim();

    const inputError = validate({
      target: event.target,
      constraints: constraints || {},
      locale,
    });

    onError({ name, error: inputError });
    onBlurCallback?.({ name, value: updatedText });
  };

  const handleClearInput = () => {
    handleValidateOnChange('');
  };

  return (
    <TextInput
      autoFocus={autoFocus}
      className={className}
      classNameInput={classNameInput}
      classNameLabel={classNameLabel}
      editable={editable}
      error={error}
      handleClearInput={handleClearInput}
      hasClearButton={hasClearButton}
      helpText={helpText}
      iconEnd={iconEnd}
      iconStart={iconStart}
      isLabelEnabled={isLabelEnabled}
      label={label}
      labelPostFix={labelPostFix}
      locale={locale}
      maxLength={maxLength}
      minLength={minLength}
      name={name}
      onBlur={handleInputBlur}
      onChange={handleInputChanged}
      onFocus={handleInputFocus}
      placeholder={placeholder}
      required={required}
      showIsRequired={showIsRequired}
      subLabel={subLabel}
      type={type}
      value={inputValue || ''}
      valuePrefix={valuePrefix}
    />
  );
}
